import className from 'classnames/bind';
import { useEffect, useState, useRef } from 'react';
import { RhLink } from "@components";
import { useViewSize } from '@/providers/ViewSizeContext';

import styles from './InducteeInfluence.module.scss';
const cx = className.bind(styles);

type InducteeInfluenceNameProps = {
  target: HTMLImageElement;
  position?: 'left' | 'right';
  name: string;
  url?: string;
  index: number;
  imageLoaded: boolean;
  count: number;
}

export default function Name({
  name,
  position = 'left',
  target,
  count,
  index,
  imageLoaded,
  url
}: InducteeInfluenceNameProps) {
  const root = useRef<HTMLDivElement>(null);
  const [lineStyles, setLineStyles] = useState<{ width: number; transform: string; }>({ width: 0, transform: 'rotate(0deg)' });
  const isLeft = position === 'left';
  const { viewWidth } = useViewSize();

  const getMargin = () => {
    const maxMargin = 15;
    const middleIndex = (count - 1) / 2;
    const distanceFromCenter = Math.abs(index - middleIndex) / middleIndex;
    const margin = distanceFromCenter * maxMargin;
    return `${margin}rem`;
  };

  const calculateLine = () => {
    if (!root.current || !target) return;

    const rootRect = root.current.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    const startX = isLeft ? rootRect.right : rootRect.left;
    const startY = rootRect.bottom;

    const endX = targetRect.left + targetRect.width / 2;
    const endY = targetRect.top + targetRect.height / 2;

    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const width = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);

    // console.log(root.current, target, isLeft);

    setLineStyles({ width, transform: `rotate(${angle}deg)` });
  };

  useEffect(() => {
    // Double requestAnimationFrame for extra layout settling
    // This is to ensure the DOM is fully rendered before calculating positions
    let raf1: number, raf2: number;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        calculateLine();
      });
    });
    // Window resize listener for recalculation
    // This ensures the line is recalculated on window resize
    const handleResize = () => {
      calculateLine();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', handleResize);
    };
  }, [target, viewWidth, imageLoaded]);

  return (
    <div
      className={cx(["inductee-influence-name"] )}
      ref={root}
      style={{
        marginLeft: isLeft ? getMargin() : 0,
        marginRight: isLeft ? 0 : getMargin(),
      }}
    >
      { url ? (
        <RhLink href={url} className={cx(["text-m", "link"])}>{name}</RhLink>
      ) : (
        <p className={cx("text-m")}>{name}</p>
      )}
      <span className={cx("line", { right: !isLeft })} style={lineStyles}/>
    </div>
  )
};
